import React from 'react';
import shortid from 'shortid';
import isBlank from '../utils/isBlank';
import findData from './findData';
import findDefinition from './findDefinition';
import formatData, { NO_DATA_DEFAULT } from './formatData';

const resolveComponent = (type, config) => {
  const {
    accessToken,
    definitions,
    displaySchema,
    schema,
    data: payloadData,
    path,
    onError,
    shouldDisplayBlankFields = false,
    enableCopyField,
    customCopyFieldComponent,
    ownProps: rootProps,
  } = config;
  const { Component, ownProps } = findDefinition(definitions, schema, type);
  let data;
  let formatted;

  try {
    data = findData(payloadData, path);

    if (!shouldDisplayBlankFields && isBlank(data)) {
      return null;
    }

    if (!data && schema.default) {
      data = schema.default;
    } else if (!data && data !== 0 && !schema.default) {
      // don't render the field at all.
      return null;
    }

    formatted = formatData(schema, data);
  } catch (error) {
    data = NO_DATA_DEFAULT;
    formatted = NO_DATA_DEFAULT;
    onError(error);
  }

  return {
    Component,
    componentProps: {
      accessToken,
      displaySchema,
      schema,
      data: formatted,
      type,
      path,
      ownData: data,
      ownProps,
      rootProps,
      ownDefinitions: definitions,
      enableCopyField,
      customCopyFieldComponent,
    },
  };
};

/**
 * Render a field without creating a new wrapper component type. Long-lived
 * media must preserve its component identity across live data updates.
 */
export const createComponentElement = (type, config, children) => {
  const resolved = resolveComponent(type, config);
  if (!resolved) {
    return null;
  }
  const { Component, componentProps } = resolved;
  return (
    <Component key={config.path.join('.')} {...componentProps}>
      {children}
    </Component>
  );
};

const createComponent = (type, config) => {
  const resolved = resolveComponent(type, config);
  if (!resolved) {
    return () => null;
  }
  const { Component, componentProps } = resolved;
  return ({ children }) => (
    <Component key={shortid.generate()} {...componentProps}>
      {children}
    </Component>
  );
};

export default createComponent;
